import React from 'react';
import { css } from '@emotion/react';
import { PersonHelpers, PersonQueries, PersonTypes } from '@frontend/api-person';
import { AvatarCropper, readFileAsDataUrl } from '@frontend/avatar-image-cropper';
import { useGetDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { Photos, usePhotoStore } from '@frontend/photos';
import { useCallPopStateSync } from '@frontend/pop';
import { useContactPanelStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Avatar, Text, SkeletonLoader, styles, CopyableText, useTooltip } from '@frontend/design-system';
import { ContactActions } from '../contact-actions/contact-actions';
import { HouseholdGuardianBadge } from '../contact-details-sections/sections/patient-list';
import { fadeInAnimation } from '../contact-panel.styles';
import { PmsButton } from './pms-button';

type CustomPanelHeaderProps = {
  person: PersonTypes.Person;
  personLocationID?: string;
  onClose?: () => void;
  showCloseButton?: boolean;
} & Omit<React.ComponentProps<typeof Avatar>, 'src' | 'size'>;

const headerStyles = {
  userSection: css`
    margin-left: ${theme.spacing(2)};
    align-self: center;
    overflow: hidden;
    flex-grow: 1;
  `,
  userHeading: css([
    fadeInAnimation,
    css`
      line-height: 32px;
      max-width: 100%;
      min-width: 0;
      flex-shrink: 1;
    `,
  ]),
  actionsSection: css`
    margin-left: ${theme.spacing(3)};
    margin-bottom: ${theme.spacing(2)};
  `,
};
export const ContactPanelHeader = ({
  person,
  personLocationID,
  showCloseButton = true,
  onClose,
  children,
  ...rest
}: CustomPanelHeaderProps) => {
  const { householdId } = useContactPanelStore();
  const isCustomContact = PersonTypes.PersonSourceType.CustomContact === person.SourceType;
  const setPhotoRecord = usePhotoStore((state) => state.setPhotoRecord);
  const { mutateAsync: uploadMutation, isLoading: uploadLoading } = PersonQueries.useUploadPersonImage();
  const { t } = useTranslation('contacts');
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { setOutlet } = useCallPopStateSync();
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  const { firstName, lastName, fullName } = useGetDisplayName({
    person: {
      firstName: person.FirstName,
      lastName: person.LastName,
      preferredName: person.PreferredName,
    },
  });

  return (
    <div>
      <SurfaceHeader.Root noBorder>
        <SurfaceHeader.Row css={{ maxHeight: 'none', alignItems: 'flex-start' }}>
          <SurfaceHeader.Start>
            <SkeletonLoader shape='circle' width={60} height={60}>
              {isCustomContact ? (
                <AvatarCropper
                  PersonID={person.PersonID}
                  FirstName={firstName}
                  LastName={lastName}
                  css={{ padding: theme.spacing(0.5) }}
                  onCropComplete={(croppedBlob) => {
                    const personId = person.PersonID;
                    if (!personId) {
                      throw new Error('no person id');
                    }
                    readFileAsDataUrl(croppedBlob, (photoUrl) => setPhotoRecord(personId, photoUrl));
                    uploadMutation({ personId: person.PersonID, image: croppedBlob });
                  }}
                  loading={uploadLoading}
                />
              ) : (
                <Photos.ProfilePhoto
                  disableClick
                  size='large'
                  css={[fadeInAnimation, { padding: theme.spacing(0.5) }]}
                  name={`${fullName}${householdId === person.PMID ? ' (HoH)' : ''}`}
                  personId={person.PersonID}
                  {...rest}
                >
                  {children}
                  {householdId === person.PMID && <HouseholdGuardianBadge />}
                </Photos.ProfilePhoto>
              )}
            </SkeletonLoader>
          </SurfaceHeader.Start>
          <SurfaceHeader.Title
            css={{
              fontSize: theme.fontSize(24),
              lineHeight: '40px',
              maxWidth: '70%',
              flexDirection: 'column',
              alignItems: 'flex-start',
              display: 'flex',
              padding: 0,
            }}
          >
            <CopyableText
              trackingId='contact-panel-header-copy-id'
              tooltipSuccessText={t('ID copied')}
              tooltipText={t('Copy ID')}
              css={detailsStyles.copyText}
              text={person.PMDisplayID}
              size='small'
              color='subdued'
            >
              #{person.PMDisplayID}
            </CopyableText>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: '100%',
              }}
            >
              <Tooltip {...tooltipProps}>
                <span>{firstName}</span>{' '}
                <span
                  css={css`
                    font-weight: ${theme.font.weight.regular};
                  `}
                >
                  {lastName}
                </span>
              </Tooltip>
              <SkeletonLoader isText width={180}>
                <div {...triggerProps} css={[styles.truncate, { maxWidth: '100%' }]}>
                  <span>{firstName}</span>{' '}
                  <span
                    css={css`
                      font-weight: ${theme.font.weight.regular};
                    `}
                  >
                    {lastName}
                  </span>
                </div>
              </SkeletonLoader>
              {personLocationID && (
                <PmsButton
                  locationId={personLocationID}
                  sourceId={person.SourceID}
                  personPMId={person.PMDisplayID || person.PMID}
                />
              )}
            </div>
            <ContactAdditionalDetails person={person} />
          </SurfaceHeader.Title>
          {showCloseButton && (
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon
                label='Close panel'
                onClick={() => {
                  setOutlet('queue');
                  setShow(false);
                  onClose?.();
                }}
                iconName='x'
              />
            </SurfaceHeader.Actions>
          )}
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <section css={headerStyles.actionsSection}>
        <ContactActions person={person} locationId={personLocationID} />
      </section>
    </div>
  );
};

const detailsStyles = {
  root: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    font-weight: ${theme.font.weight.regular};
    & > * {
      line-height: 20px;
    }
  `,
  copyText: [
    styles.truncate,
    css`
      max-width: 80px;
      flex-grow: 0;
      flex-basis: content;
      font-size: ${theme.fontSize(14)};
      font-weight: ${theme.font.weight.regular};
    `,
  ],
};

const ContactAdditionalDetails = ({ person }: { person: PersonTypes.Person }) => {
  const age = PersonHelpers.getAge(person);
  const { t } = useTranslation('contacts');
  if (person.SourceType === undefined) {
    return null;
  }
  return (
    <SkeletonLoader
      css={css`
        margin-top: ${theme.spacing(1)};
      `}
      isText
      width={100}
    >
      <div css={detailsStyles.root}>
        {!!person.Status && (
          <Text size='medium' color='subdued' title={person.Status}>
            {person.Status}
          </Text>
        )}
        {!!person.Gender && (
          <Text size='medium' color='subdued'>
            {person.Gender}
          </Text>
        )}
        {!!age && (
          <Text size='medium' color='subdued'>
            {t('{{age}} yrs', { age })}
          </Text>
        )}
      </div>
    </SkeletonLoader>
  );
};
